const ALLOWED_TAGS = new Set(['SPAN', 'BR', 'EM']);
const ALLOWED_CLASSES = new Set(['home-cta__title-accent']);
const ACCENT_CLASS = 'home-cta__title-accent';
const MARKUP_PATTERN = /<(span|br|em)\b/i;
/** Detecta «para tu negocio» al final del título (plain text). */
const ACCENT_PLAIN_PATTERN = /(para\s+tu\s+negocio)\s*$/i;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function wrapPlainAccent(escapedOrSafeText: string): string {
  const match = ACCENT_PLAIN_PATTERN.exec(escapedOrSafeText);
  if (!match || match.index === undefined) {
    return escapedOrSafeText;
  }
  const before = escapedOrSafeText.slice(0, match.index);
  const accent = match[1];
  return `${before}<span class="${ACCENT_CLASS}">${accent}</span>`;
}

function serializeAllowedNodes(parent: ParentNode): string {
  let html = '';

  parent.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      html += escapeHtml(node.textContent ?? '');
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;
    const tag = element.tagName;

    if (!ALLOWED_TAGS.has(tag)) {
      html += serializeAllowedNodes(element);
      return;
    }

    if (tag === 'BR') {
      html += '<br>';
      return;
    }

    const classes = [...element.classList].filter((className) =>
      ALLOWED_CLASSES.has(className)
    );
    const classAttr = classes.length ? ` class="${classes.join(' ')}"` : '';
    const tagName = tag.toLowerCase();

    html += `<${tagName}${classAttr}>${serializeAllowedNodes(element)}</${tagName}>`;
  });

  return html;
}

/**
 * Aplica degradado: si hay HTML permitido lo sanitiza;
 * si es texto plano, envuelve «para tu negocio» en el span accent.
 */
export function prepareVisionTitleHtml(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return '';
  }

  if (!MARKUP_PATTERN.test(trimmed)) {
    return wrapPlainAccent(escapeHtml(trimmed));
  }

  if (typeof DOMParser === 'undefined') {
    return wrapPlainAccent(escapeHtml(trimmed));
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  const sanitized = serializeAllowedNodes(doc.body).trim();
  if (sanitized.includes(ACCENT_CLASS)) {
    return sanitized;
  }
  return wrapPlainAccent(sanitized);
}

const BUTTON_ALLOWED_TAGS = new Set(['EM', 'BR']);
const BUTTON_MARKUP_PATTERN = /<(em|br)\b/i;

function serializeAllowedButtonNodes(parent: ParentNode): string {
  let html = '';

  parent.childNodes.forEach((node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      html += escapeHtml(node.textContent ?? '');
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    const element = node as HTMLElement;
    const tag = element.tagName;

    if (!BUTTON_ALLOWED_TAGS.has(tag)) {
      html += serializeAllowedButtonNodes(element);
      return;
    }

    if (tag === 'BR') {
      html += '<br>';
      return;
    }

    const tagName = tag.toLowerCase();
    html += `<${tagName}>${serializeAllowedButtonNodes(element)}</${tagName}>`;
  });

  return html;
}

/**
 * Nombre del botón administrable: HTML acotado (`em`, `br`).
 * Ejemplo Figma: `Iniciar impulso <em>ahora</em>`
 */
export function prepareVisionButtonHtml(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) {
    return '';
  }

  if (!BUTTON_MARKUP_PATTERN.test(trimmed)) {
    return escapeHtml(trimmed);
  }

  if (typeof DOMParser === 'undefined') {
    return escapeHtml(trimmed);
  }

  const doc = new DOMParser().parseFromString(trimmed, 'text/html');
  return serializeAllowedButtonNodes(doc.body).trim();
}

/** Texto plano para aria-label / accesibilidad. */
export function stripVisionHtml(raw: string): string {
  return (raw ?? '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
